import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { ContentHeader, QueryStatus, useToast } from "@cx/ui";
import type {
  ContactListItem,
  LabelPrintFormat,
  LabelTemplatesView,
  ListResult,
  ProductListItem,
} from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import {
  LABEL_SOURCE_LABELS,
  LABELS_SYNC_MAX,
  MAX_LABEL_COPIES,
  MAX_LABEL_RECORDS,
  MAX_LABELS_PER_PRINT,
  formatDocument,
  labelsPerPage,
  type LabelSource,
} from "@cx/domain";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";
import { postApiFile } from "../../download";
import { takeLabelItems, type LabelPrintItem } from "./mm";
import { layoutSummary } from "./LabelTemplatesPage";

type Found = { id: string; name: string; detail: string };

/**
 * Impressão de etiquetas (T3.6): escolhe o modelo, os registros (busca ou seleção vinda da lista de produtos) e as
 * cópias; PDF para folha/jato de tinta ou ZPL para térmica. Até 300 etiquetas o arquivo baixa na hora; acima, vai
 * para Ações assíncronas.
 */
export function LabelPrintPage(): ReactElement {
  const search = useSearch({ strict: false }) as { modelo?: string; fonte?: LabelSource };
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const [templateId, setTemplateId] = useState(search.modelo ?? "");
  const [items, setItems] = useState<LabelPrintItem[]>([]);
  const [q, setQ] = useState("");
  const [format, setFormat] = useState<LabelPrintFormat>("pdf");
  const [skip, setSkip] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handed = takeLabelItems();
    if (handed.length) setItems(handed);
  }, []);

  const templates = useQuery({
    queryKey: ["label-templates", companyId],
    queryFn: () => api<LabelTemplatesView>("/label-templates"),
  });
  const list = useMemo(() => templates.data?.data ?? [], [templates.data]);
  const wanted = search.fonte;
  // Sem modelo escolhido: o primeiro da fonte pedida (ou o primeiro da lista).
  useEffect(() => {
    if (templateId || !list.length) return;
    const t = list.find((x) => !wanted || x.source === wanted) ?? list[0]!;
    setTemplateId(t.id);
  }, [list, templateId, wanted]);
  const template = list.find((t) => t.id === templateId);
  const source = template?.source ?? wanted ?? "products";
  const canReadSource = can(perms, source, "read");

  const found = useQuery({
    queryKey: ["label-print-search", companyId, source, q],
    enabled: q.trim().length >= 2 && canReadSource,
    queryFn: async (): Promise<Found[]> => {
      const params = new URLSearchParams({ q: q.trim(), limit: "20" });
      if (source === "products") {
        const r = await api<ListResult<ProductListItem>>(`/products?${params}`);
        return r.data.map((p) => ({ id: p.id, name: p.name, detail: p.sku ? `SKU ${p.sku}` : "" }));
      }
      const r = await api<ListResult<ContactListItem>>(`/contacts?${params}`);
      return r.data.map((c) => ({
        id: c.id,
        name: c.name,
        detail: [c.document ? formatDocument(c.document) : "", c.city && c.uf ? `${c.city}/${c.uf}` : ""]
          .filter(Boolean)
          .join(" · "),
      }));
    },
  });

  const total = items.reduce((a, i) => a + i.copies, 0);
  const print = useMutation({
    mutationFn: () =>
      postApiFile(`/label-templates/${templateId}/print`, companyId, `etiquetas.${format}`, {
        items: items.map((i) => ({ id: i.id, copies: i.copies })),
        format,
        skip: template?.layout.page.kind === "sheet" ? skip : 0,
      }),
    onSuccess: (r) => {
      setError(null);
      if ("jobId" in r) {
        toast("info", "Etiquetas na fila. Baixe o arquivo em Ações assíncronas, no topo da página.");
        qc.invalidateQueries({ queryKey: ["jobs", companyId] });
      } else toast("success", `Arquivo ${r.name} gerado`);
    },
    onError: (e: Error) => setError(errorMessage(e)),
  });

  const add = (f: Found) => {
    if (items.some((i) => i.id === f.id) || items.length >= MAX_LABEL_RECORDS) return;
    setItems([...items, { ...f, copies: 1 }]);
  };

  return (
    <>
      <ContentHeader title="Imprimir etiquetas" breadcrumb={["Cadastros", "Etiquetas", "Imprimir"]} />
      <QueryStatus loading={templates.isLoading} error={templates.error} />
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      {templates.isSuccess && list.length === 0 && (
        <div className="alert alert-warning">
          Nenhum modelo de etiqueta cadastrado. Crie um em Cadastros › Etiquetas.
        </div>
      )}
      {list.length > 0 && (
        <div className="row g-3">
          <div className="col-lg-4">
            <div className="box card p-3">
              <label className="form-label" htmlFor="lp-template">
                Modelo
              </label>
              <select
                id="lp-template"
                className="form-select"
                value={templateId}
                onChange={(e) => {
                  const t = list.find((x) => x.id === e.target.value);
                  if (t && template && t.source !== template.source) setItems([]);
                  setTemplateId(e.target.value);
                }}
              >
                {list.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({LABEL_SOURCE_LABELS[t.source]})
                  </option>
                ))}
              </select>
              {template && <div className="small text-muted mt-1">{layoutSummary(template.layout)}</div>}
              <label className="form-label mt-3" htmlFor="lp-format">
                Formato
              </label>
              <select
                id="lp-format"
                className="form-select"
                value={format}
                onChange={(e) => setFormat(e.target.value as LabelPrintFormat)}
              >
                <option value="pdf">PDF (impressora comum)</option>
                <option value="zpl">ZPL (impressora térmica Zebra/Elgin/Argox)</option>
              </select>
              {template?.layout.page.kind === "sheet" && format === "pdf" && (
                <>
                  <label className="form-label mt-3" htmlFor="lp-skip">
                    Pular etiquetas já usadas na 1ª folha
                  </label>
                  <input
                    id="lp-skip"
                    type="number"
                    className="form-control"
                    min={0}
                    max={labelsPerPage(template.layout) - 1}
                    value={skip}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isInteger(v) && v >= 0 && v < labelsPerPage(template.layout)) setSkip(v);
                    }}
                  />
                </>
              )}
              <hr />
              <p className="small mb-2" data-testid="label-total">
                {total.toLocaleString("pt-BR")} etiqueta(s) de {items.length} registro(s).
                {total > LABELS_SYNC_MAX && " Acima de 300, o arquivo é gerado na fila."}
              </p>
              <button
                type="button"
                className="btn btn-primary w-100"
                disabled={
                  !template ||
                  items.length === 0 ||
                  total > MAX_LABELS_PER_PRINT ||
                  print.isPending ||
                  !canReadSource
                }
                onClick={() => print.mutate()}
              >
                {print.isPending ? "Gerando…" : "Imprimir"}
              </button>
              {total > MAX_LABELS_PER_PRINT && (
                <p className="small text-danger mt-2 mb-0">
                  Até {MAX_LABELS_PER_PRINT.toLocaleString("pt-BR")} etiquetas por impressão.
                </p>
              )}
            </div>
          </div>
          <div className="col-lg-8">
            <div className="box card p-3">
              {!canReadSource ? (
                <div className="alert alert-warning mb-0">
                  Seu perfil não pode ver {LABEL_SOURCE_LABELS[source].toLowerCase()}.
                </div>
              ) : (
                <>
                  <label className="form-label" htmlFor="lp-search">
                    Buscar{" "}
                    {source === "products" ? "produto (nome, SKU ou GTIN)" : "contato (nome ou documento)"}
                  </label>
                  <input
                    id="lp-search"
                    className="form-control"
                    value={q}
                    maxLength={100}
                    onChange={(e) => setQ(e.target.value)}
                  />
                  {found.data && found.data.length > 0 && (
                    <ul className="list-group mt-2" aria-label="Resultados da busca">
                      {found.data.map((f) => (
                        <li
                          key={f.id}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          <span>
                            {f.name} <span className="small text-muted">{f.detail}</span>
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            disabled={items.some((i) => i.id === f.id)}
                            onClick={() => add(f)}
                          >
                            Adicionar
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {found.data && found.data.length === 0 && (
                    <p className="small text-muted mt-2 mb-0">Nada encontrado.</p>
                  )}
                </>
              )}
              <h2 className="h6 mt-3">Registros a imprimir</h2>
              {items.length === 0 ? (
                <p className="small text-muted mb-0">
                  Busque e adicione registros, ou selecione na lista de produtos.
                </p>
              ) : (
                <table className="table table-sm align-middle mb-0" aria-label="Registros a imprimir">
                  <thead>
                    <tr>
                      <th scope="col">Registro</th>
                      <th scope="col" style={{ width: 110 }}>
                        Cópias
                      </th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={it.id}>
                        <td>
                          {it.name} <span className="small text-muted">{it.detail}</span>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            aria-label={`Cópias de ${it.name}`}
                            min={1}
                            max={MAX_LABEL_COPIES}
                            value={it.copies}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              if (Number.isInteger(v) && v >= 1 && v <= MAX_LABEL_COPIES)
                                setItems(items.map((x, j) => (j === i ? { ...x, copies: v } : x)));
                            }}
                          />
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            aria-label={`Remover ${it.name}`}
                            onClick={() => setItems(items.filter((_, j) => j !== i))}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
